import { useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  useLazyGetPointsDashboardDataQuery,
  useLazyGetNotificationsDashboardDataQuery,
  useLazyGetPointsActivityQuery,
  useLazyGetNotificationsSentVsOpenedQuery,
  useLazyGetTopCustomersQuery,
  useLazyGetSessionsActivityQuery,
  useLazyGetDailyUsageReportsQuery,
  useLazyGetTransactionsDashboardQuery,
  useLazyGetUpcomingBirthdayListQuery,
  useLazyGetBirthdayNotificationsListQuery,
} from "../../services/dashboard";
import { useEffect, useMemo, useState } from "react";
import { CUSTOMER_TYPE } from "../../constants/enums";
import BirthdayListModal from "../../components/BirthdayListModal";
import { useLazyGetUserListQuery } from "../../services/user";
import { showError } from "../../helpers/showToast";
interface LocationWisePointsSummary {
  location: string;
  pointsEarned: number;
  pointsExpired: number;
  pointsRedeemed: number;
}

// Referral Performance Data
const referralSummary = [
  {
    id: "totalSent",
    label: "Total Referrals Sent",
    value: "0",
    helper: "members who referred",
  },
  {
    id: "successful",
    label: "Successful Referrals",
    value: "0",
    helper: "conversion rate 0%",
  },
  {
    id: "points",
    label: "Points Collected",
    value: "0",
    helper: "from referrals",
  },
];

const referralActivity = [
  { day: "Wed", sent: 0, converted: 0, pointsEarned: 0 },
  { day: "Thu", sent: 0, converted: 0, pointsEarned: 0 },
  { day: "Fri", sent: 0, converted: 0, pointsEarned: 0 },
  { day: "Sat", sent: 0, converted: 0, pointsEarned: 0 },
  { day: "Sun", sent: 0, converted: 0, pointsEarned: 0 },
  { day: "Mon", sent: 0, converted: 0, pointsEarned: 0 },
  { day: "Tue", sent: 0, converted: 0, pointsEarned: 0 },
];

const referralAverages = [
  { id: "avgSent", label: "Avg. Sent/Day", value: "0" },
  { id: "avgConverted", label: "Avg. Converted/Day", value: "0" },
  { id: "avgPoints", label: "Avg. Points/Day", value: "0" },
];

const CHART_COLORS = ["#2F66F6", "#34B53A", "#FF8A48"];
interface Customer {
  rank?: number;
  name?: string;
  mobile?: string;
  logins?: number;
  lastLoginActivity?: string;
  // Add other customer properties as needed
}
interface PlatformDistribution {
  android: number;
  ios: number;
}

interface VersionDistribution {
  users: number;
  appVersion: string;
}

interface SessionsActivityResponse {
  platformDistribution: PlatformDistribution;
  versionDistribution: VersionDistribution[];
}
interface VersionUsage {
  version: string;
  users: number;
  percentage: string;
}

interface DailyTrendItem {
  date: string;
  count: number;
}

interface DailyUsageResponse {
  usersOpenedToday: number;
  averageDailyUsers: number;
  dailyTrend: DailyTrendItem[];
}
interface DailyActiveUser {
  day: string;
  users: number;
}

interface PlatformStat {
  id: "android" | "ios" | "total";
  label: string;
  value: number;
  helper: string;
}

const Dashboard = () => {
  const navigate = useNavigate();
  const [pointsSummaryData, setPointsSummaryData] = useState({
    pointsEarned: 0,
    pointsRedeemed: 0,
    pointsExpired: 0,
    currentBalance: 0,
    totalUsers: 0,
  });
  const [notificationsSummaryData, setNotificationsSummaryData] = useState({
    notificationsSent: 0,
    notificationsOpened: {
      count: 0,
      ctr: 0,
    },
    upcomingBirthdays: 0,
    birthdayNotifications: 0,
  });
  const [dateRange, setDateRange] = useState<string>("custom");
  const [pointsActivity, setPointsActivity] = useState([]);
  const [pointsTrend, setPointsTrend] = useState([]);
  const [notificationsComparison, setNotificationsComparison] = useState([]);
  const [activeCustomers, setActiveCustomers] = useState<Customer[]>([]);
  const [inactiveCustomers, setInactiveCustomers] = useState<Customer[]>([]);
  const [platformStats, setPlatformStats] = useState<PlatformStat[]>([]);
  const [platformDistribution, setPlatformDistribution] = useState<
    { name: string; value: number }[]
  >([]);
  const [dailyUsageStats, setDailyUsageStats] = useState([
    {
      id: "today",
      label: "Users Opened App Today",
      value: "0",
      helper: "unique users today",
    },
    {
      id: "average",
      label: "Average Daily Users",
      value: "0",
      helper: "average for selected period",
    },
  ]);
  const [versionUsage, setVersionUsage] = useState<VersionUsage[]>([]);
  const [dailyActiveUsers, setDailyActiveUsers] = useState<DailyActiveUser[]>(
    []
  );
  const [locationWisePointsSummary, setLocationWisePointsSummary] = useState<
    LocationWisePointsSummary[]
  >([]);
  const [upcomingBirthdays, setUpcomingBirthdays] = useState<any[]>([]);
  const [isBirthdayModalOpen, setIsBirthdayModalOpen] = useState(false);

  const getFirstDayOfMonth = () => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1)
      .toISOString()
      .split("T")[0];
  };

  const [customDateRange, setCustomDateRange] = useState({
    startDate: getFirstDayOfMonth(),
    endDate: new Date().toISOString().split("T")[0],
  });

  const [totalCount, setTotalCount] = useState(0);
  const [deletedCount, setDeletedCount] = useState(0);

  const [fetchDashboardData] = useLazyGetPointsDashboardDataQuery();
  const [fetchNotificationsDashboardData] =
    useLazyGetNotificationsDashboardDataQuery();
  const [fetchPointsActivityData] = useLazyGetPointsActivityQuery();
  const [fetchNotificationsSentVsOpenedData] =
    useLazyGetNotificationsSentVsOpenedQuery();
  const [fetchTopCustomersData] = useLazyGetTopCustomersQuery();
  const [fetchSessionsActivityData] = useLazyGetSessionsActivityQuery();
  const [fetchDailyUsageReportsData] = useLazyGetDailyUsageReportsQuery();
  const [fetchTransactionsDashboardData] =
    useLazyGetTransactionsDashboardQuery();
  const [fetchUpcomingBirthdayListData] = useLazyGetUpcomingBirthdayListQuery();
  const [getUserList] = useLazyGetUserListQuery();

  useEffect(() => {
    loadDashboardData();
    loadNotificationsDashboardData();
    loadPointsActivityData();
    loadNotificationsSentVsOpenedData();
    loadTopActiveCustomersData();
    loadTopInactiveCustomersData();
    loadSessionsActivityData();
    loadDailyUsageReportsData();
    loadUpcomingBirthdayListData();
    fetchUserCount();
    fetchDeletedUserCount();
  }, [fetchDashboardData]);

  useEffect(() => {
    if (
      dateRange === "custom" &&
      (!customDateRange.startDate || !customDateRange.endDate)
    ) {
      return;
    }
    loadDashboardData();
    loadNotificationsDashboardData();
    loadPointsActivityData();
    loadNotificationsSentVsOpenedData();
    loadTopActiveCustomersData();
    loadTopInactiveCustomersData();
    loadSessionsActivityData();
    loadDailyUsageReportsData();
    loadTransactionsDashboardData();
    loadUpcomingBirthdayListData();
  }, [dateRange, customDateRange]);

  const loadDashboardData = async () => {
    try {
      const response = await fetchDashboardData({ type: dateRange }).unwrap();
      if (response?.data) {
        const {
          pointsEarned = 0,
          pointsRedeemed = 0,
          pointsExpired = 0,
          currentBalance = 0,
          totalUsers = 0,
        } = response.data;

        setPointsSummaryData({
          pointsEarned: Number(pointsEarned) || 0,
          pointsRedeemed: Number(pointsRedeemed) || 0,
          pointsExpired: Number(pointsExpired) || 0,
          currentBalance: Number(currentBalance) || 0,
          totalUsers: Number(totalUsers) || 0,
        });
      }
    } catch (err) {
      console.error("Failed to fetch dashboard data:", err);
      // Consider showing an error message to the user
    }
  };

  const loadNotificationsDashboardData = async () => {
    try {
      const response = await fetchNotificationsDashboardData({
        type: dateRange,
        startDate:
          dateRange === "custom" ? customDateRange.startDate : undefined,
        endDate: dateRange === "custom" ? customDateRange.endDate : undefined,
      }).unwrap();
      console.log("response", response);
      if (response?.data) {
        const {
          notificationsSent = 0,
          notificationsOpened = {
            count: 0,
            ctr: 0,
          },
          upcomingBirthdays = 0,
          birthdayNotifications = 0,
        } = response.data;

        setNotificationsSummaryData({
          notificationsSent: Number(notificationsSent) || 0,
          notificationsOpened: notificationsOpened,
          birthdayNotifications: Number(birthdayNotifications) || 0,
          upcomingBirthdays: Number(upcomingBirthdays) || 0,
        });
      }
    } catch (err) {
      console.error("Failed to fetch dashboard data:", err);
      // Consider showing an error message to the user
    }
  };

  const loadPointsActivityData = async () => {
    try {
      const response = await fetchPointsActivityData({
        type: dateRange !== "alltime" ? dateRange : "weekly",
        startDate:
          dateRange === "custom" ? customDateRange.startDate : undefined,
        endDate: dateRange === "custom" ? customDateRange.endDate : undefined,
      }).unwrap();

      if (!response?.data) return;

      const formattedData = response.data.map((item: any) => {
        const date = new Date(item.date);
        const day = date.toLocaleDateString("en-US", { weekday: "short" });

        return {
          day,
          date: item.date,
          earned: Number(item.earned) || 0,
          redeemed: Number(item.redeemed) || 0,
          expired: Number(item.expired) || 0,
        };
      });

      setPointsActivity(formattedData);
      setPointsTrend(
        formattedData.map(({ day, earned, redeemed }: any) => ({
          day,
          earned,
          redeemed,
        }))
      );
    } catch (err) {
      console.error("Failed to fetch points activity data:", err);
      // Consider showing an error message to the user
    }
  };

  const loadNotificationsSentVsOpenedData = async () => {
    try {
      const response = await fetchNotificationsSentVsOpenedData({
        type: dateRange,
        startDate:
          dateRange === "custom" ? customDateRange.startDate : undefined,
        endDate: dateRange === "custom" ? customDateRange.endDate : undefined,
      }).unwrap();
      console.log("response", response);
      if (response?.data) {
        const formattedData = response.data.map((item: any) => {
          return {
            day: item.day,
            sent: Number(item.sent) || 0,
            opened: Number(item.opened) || 0,
          };
        });
        setNotificationsComparison(formattedData);
      }
    } catch (err) {
      console.error("Failed to fetch dashboard data:", err);
      // Consider showing an error message to the user
    }
  };

  const loadTopActiveCustomersData = async () => {
    try {
      const response = await fetchTopCustomersData({
        type: CUSTOMER_TYPE.LEAST_INACTIVE,
      }).unwrap();

      if (!response?.data) return;
      setActiveCustomers(response.data as Customer[]);
    } catch (err) {
      console.error("Failed to fetch top customers data:", err);
      // Consider showing an error message to the user
    }
  };

  const loadTopInactiveCustomersData = async () => {
    try {
      const response = await fetchTopCustomersData({
        type: CUSTOMER_TYPE.LEAST_ACTIVE,
      }).unwrap();

      if (!response?.data) return;
      setInactiveCustomers(response.data as Customer[]);
    } catch (err) {
      console.error("Failed to fetch top customers data:", err);
      // Consider showing an error message to the user
    }
  };

  const loadSessionsActivityData = async () => {
    try {
      const { data } = await fetchSessionsActivityData({
        type: dateRange,
        startDate:
          dateRange === "custom" ? customDateRange.startDate : undefined,
        endDate: dateRange === "custom" ? customDateRange.endDate : undefined,
      }).unwrap();

      if (!data) {
        console.warn("No data received from sessions activity API");
        return;
      }

      const {
        platformDistribution = { android: 0, ios: 0 },
        versionDistribution = [],
      } = (data as SessionsActivityResponse) || {};

      // Format platform distribution for the UI
      const formattedPlatformDistribution = [
        { name: "Android", value: platformDistribution.android },
        { name: "iOS", value: platformDistribution.ios },
      ];

      // Format version distribution for the UI
      const formattedVersionDistribution = versionDistribution.map((item) => ({
        version: item.appVersion || "Unknown",
        users: item.users || 0,
        percentage: "0%",
      }));

      const totalUsers =
        platformDistribution.android + platformDistribution.ios;

      const platformStates: PlatformStat[] = [
        {
          id: "android",
          label: "Android Users",
          value: platformDistribution.android,
          helper:
            totalUsers > 0
              ? `${((platformDistribution.android / totalUsers) * 100).toFixed(
                  1
                )}% of total users`
              : "0.0% of total users",
        },
        {
          id: "ios",
          label: "iOS Users",
          value: platformDistribution.ios,
          helper:
            totalUsers > 0
              ? `${((platformDistribution.ios / totalUsers) * 100).toFixed(
                  1
                )}% of total users`
              : "0.0% of total users",
        },
        {
          id: "total",
          label: "Total Users",
          value: totalUsers,
          helper: "all platforms",
        },
      ];

      setPlatformDistribution(formattedPlatformDistribution);
      setVersionUsage(formattedVersionDistribution);
      setPlatformStats(platformStates);
    } catch (error) {
      console.error("Failed to fetch sessions activity data:", error);
      // Reset to default values on error
      setPlatformDistribution([
        { name: "Android", value: 0 },
        { name: "iOS", value: 0 },
      ]);
      setVersionUsage([]);
      setPlatformStats([]);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { weekday: "short" });
  };

  const loadDailyUsageReportsData = async () => {
    try {
      const { data } = await fetchDailyUsageReportsData({
        type: dateRange,
        startDate:
          dateRange === "custom" ? customDateRange.startDate : undefined,
        endDate: dateRange === "custom" ? customDateRange.endDate : undefined,
      }).unwrap();

      if (!data) {
        console.warn("No data received from daily usage reports API");
        return;
      }

      const {
        usersOpenedToday = 0,
        averageDailyUsers = 0,
        dailyTrend = [],
      } = data as DailyUsageResponse;

      setDailyUsageStats([
        {
          id: "today",
          label: "Users Opened App Today",
          value: usersOpenedToday.toLocaleString(),
          helper: "unique users today",
        },
        {
          id: "average",
          label: "Average Daily Users",
          value: averageDailyUsers.toLocaleString(),
          helper: "average for selected period",
        },
      ]);

      // Set all daily trend data for the chart
      if (dailyTrend.length > 0) {
        const formattedTrends = dailyTrend.map((item) => ({
          day: formatDate(item.date) || `Day ${item.date}`,
          users: item.count,
        }));
        setDailyActiveUsers(formattedTrends);
      }
    } catch (error) {
      console.error("Failed to fetch daily usage reports data:", error);
      // Optionally set default values or show error state
      setDailyUsageStats((prev) =>
        prev.map((stat) => ({
          ...stat,
          value: "0",
        }))
      );
    }
  };

  const loadTransactionsDashboardData = async () => {
    try {
      const { data } = await fetchTransactionsDashboardData({
        type: dateRange,
        startDate:
          dateRange === "custom" ? customDateRange.startDate : undefined,
        endDate: dateRange === "custom" ? customDateRange.endDate : undefined,
      }).unwrap();

      if (!data) {
        return;
      }
      setLocationWisePointsSummary(data || []);
    } catch (error) {
      console.error("Failed to fetch daily usage reports data:", error);
    }
  };

  const loadUpcomingBirthdayListData = async () => {
    try {
      const { data } = await fetchUpcomingBirthdayListData({
        type: dateRange,
      }).unwrap();

      if (!data) {
        setUpcomingBirthdays([]);
        return;
      }
      setUpcomingBirthdays(data);
    } catch (error) {
      console.error("Failed to fetch upcoming birthday list data:", error);
      setUpcomingBirthdays([]);
    }
  };

  const handleBirthdayListModalOpen = () => {
    console.log("Opening birthday list modal");
    setIsBirthdayModalOpen(true);
  };

  const fetchUserCount = async () => {
    try {
      const params: any = {
        page: 1,
        limit: 10,
        role: 1,
        isBlocked: "0",
        isDeleted: "0",
      };
      const response = await getUserList(params).unwrap();
      if (response?.statusCode === 200) {
        setTotalCount(response?.data?.totalCount || 0);
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to fetch Users");
    }
  };

  const fetchDeletedUserCount = async () => {
    try {
      const params: any = {
        page: 1,
        limit: 10,
        role: 1,
        isBlocked: "0",
        isDeleted: "3",
      };
      const response = await getUserList(params).unwrap();
      if (response?.statusCode === 200) {
        setDeletedCount(response?.data?.totalCount || 0);
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to fetch Users");
    }
  };

  const accountSummary = useMemo(() => {
    return [
      {
        id: "total",
        label: "Total Users",
        value: totalCount?.toString() || "0",
        helper: "registered users",
      },
      {
        id: "inactive",
        label: "Deleted Users",
        value: deletedCount?.toString() || "0",
        helper: "not active recently (deleted accounts)",
      },
    ];
  }, [totalCount, deletedCount]);

  const pointsSummary = useMemo(
    () => [
      {
        id: "earned",
        label: "Points Earned",
        value: pointsSummaryData.pointsEarned.toLocaleString(),
        helper: `from all users`,
      },
      {
        id: "redeemed",
        label: "Points Redeemed",
        value: pointsSummaryData.pointsRedeemed.toLocaleString(),
        helper: "total points used",
      },
      {
        id: "expired",
        label: "Points Expired",
        value: pointsSummaryData.pointsExpired.toLocaleString(),
        helper: "points expired",
      },
      {
        id: "balance",
        label: "Current Balance",
        value: pointsSummaryData.currentBalance.toLocaleString(),
        helper: "aggregated balance",
      },
    ],
    [pointsSummaryData]
  );

  const notificationStats = useMemo(
    () => [
      {
        id: "sent",
        label: "Notifications Sent",
        value: notificationsSummaryData.notificationsSent.toLocaleString(),
        helper: "total push messages sent",
        isClickable: false,
      },
      {
        id: "opened",
        label: "Notifications Opened",
        value:
          notificationsSummaryData.notificationsOpened.count.toLocaleString(),
        helper: notificationsSummaryData.notificationsOpened.ctr,
        isClickable: false,
      },
      {
        id: "birthdays",
        label: "Upcoming Birthdays",
        value: notificationsSummaryData.upcomingBirthdays.toLocaleString(),
        helper: "next 7 days",
        isClickable: true,
      },
      {
        id: "birthdayNotifications",
        label: "Birthday Notifications",
        value: notificationsSummaryData.birthdayNotifications.toLocaleString(),
        helper: "birthday messages sent",
        isClickable: true,
      },
    ],
    [notificationsSummaryData]
  );

  return (
    <div className="analytics-dashboard">
      {/* Header */}
      <div className="dashboard_header cards">
        <div>
          <p className="subtitle">Functional Dashboard Design</p>
          <h1>Analytics Dashboard</h1>
        </div>
        <div className="dashboard_filters">
          {/* <div className="filter_group">
            <div>
              <label htmlFor="dateRange">Date Range</label>
              <select
                id="dateRange"
                defaultValue="alltime"
                onChange={(e) => setDateRange(e.target.value)}
              >
                <option value="alltime">All Time</option>
                <option value="weekly">Last 7 Days</option>
                <option value="monthly">Last 30 Days</option>
                <option value="yearly">Last 1 Year</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
          </div> */}

          {dateRange === "custom" && (
            <div
              className="filter_group"
              style={{ display: "flex", gap: "10px", marginTop: "10px" }}
            >
              <div>
                <label htmlFor="startDate">Start Date</label>
                <input
                  type="date"
                  id="startDate"
                  value={customDateRange.startDate}
                  onChange={(e) => {
                    setCustomDateRange((prev) => ({
                      ...prev,
                      startDate: e.target.value,
                    }));
                    // Optionally trigger data fetch here or add an "Apply" button
                  }}
                  max={new Date().toISOString().split("T")[0]}
                  onKeyDown={(e) => e.preventDefault()}
                />
              </div>

              <div>
                <label htmlFor="endDate">End Date</label>
                <input
                  type="date"
                  id="endDate"
                  value={customDateRange.endDate}
                  onChange={(e) => {
                    setCustomDateRange((prev) => ({
                      ...prev,
                      endDate: e.target.value,
                    }));
                    // Optionally trigger data fetch here or add an "Apply" button
                  }}
                  max={new Date().toISOString().split("T")[0]}
                  min={customDateRange.startDate}
                  onKeyDown={(e) => e.preventDefault()}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <section>
        <h2 style={{ marginBottom: "20px", fontSize: "20px", fontWeight: 600 }}>
          Member Account
        </h2>
        <div className="panel-grid four">
          {accountSummary.map((stat) => (
            <div className="cards stat_card" key={stat.id}>
              <p className="label">{stat.label}</p>
              <h3>{stat.value}</h3>
            </div>
          ))}
        </div>
      </section>
      {/* Member Points Summary */}
      <section>
        <h2 style={{ marginBottom: "20px", fontSize: "20px", fontWeight: 600 }}>
          Member Points Summary
        </h2>
        <div className="panel-grid four">
          {pointsSummary.map((stat) => (
            <div className="cards stat_card" key={stat.id}>
              <p className="label">{stat.label}</p>
              <h3>{stat.value}</h3>
              <span className="muted">{stat.helper}</span>
            </div>
          ))}
        </div>
        <div className="panel-grid two" style={{ marginTop: "20px" }}>
          <div className="cards chart_card">
            <div className="section_heading">
              <div>
                <h3>Points Activity</h3>
                <p>Earned vs redeemed vs expired</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={pointsActivity}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="earned"
                  stackId="a"
                  fill="#34B53A"
                  radius={[8, 8, 0, 0]}
                />
                <Bar
                  dataKey="expired"
                  stackId="a"
                  fill="#FF0000"
                  radius={[0, 0, 0, 0]}
                />
                <Bar
                  dataKey="redeemed"
                  stackId="a"
                  fill="#FF8A48"
                  radius={[0, 0, 8, 8]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="cards chart_card">
            <div className="section_heading">
              <div>
                <h3>Points Trend</h3>
                <p>Earned vs redeemed over time</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={pointsTrend}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="earned"
                  stroke="#0B807F"
                  strokeWidth={3}
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="redeemed"
                  stroke="#FF8A48"
                  strokeWidth={3}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Location Wise points Issuance and Redemption */}
      <section>
        <h2 style={{ marginBottom: "20px", fontSize: "20px", fontWeight: 600 }}>
          Location Wise points Issuance and Redemption
        </h2>
        <div className="panel-grid two" style={{ marginTop: "20px" }}>
          <div className="cards chart_card" style={{ marginTop: "20px" }}>
            <div className="section_heading">
              <div>
                <h3>Points Earned </h3>
                {/* <p>Trend of daily active users over time</p> */}
              </div>
            </div>
            <div style={{ marginTop: "20px", overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr
                    style={{
                      backgroundColor: "#f8f9fa",
                      borderBottom: "1px solid #dee2e6",
                    }}
                  >
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontWeight: 600,
                      }}
                    >
                      Location
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "right",
                        fontWeight: 600,
                      }}
                    >
                      Points Earned
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {locationWisePointsSummary.map((item, index) => (
                    <tr
                      key={`earned-${index}`}
                      style={{
                        borderBottom: "1px solid #dee2e6",
                        backgroundColor: index % 2 === 0 ? "#fff" : "#f8f9fa",
                      }}
                    >
                      <td
                        style={{
                          padding: "12px",
                          borderRight: "1px solid #dee2e6",
                        }}
                      >
                        {item.location || "N/A"}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "right",
                          color: "#2F66F6",
                        }}
                      >
                        {item.pointsEarned?.toLocaleString() || "0"}
                      </td>
                    </tr>
                  ))}
                  {locationWisePointsSummary.length === 0 && (
                    <tr>
                      <td
                        colSpan={2}
                        style={{
                          padding: "20px",
                          textAlign: "center",
                          color: "#6c757d",
                        }}
                      >
                        No points earned data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="cards chart_card" style={{ marginTop: "20px" }}>
            <div className="section_heading">
              <div>
                <h3>Points Redeemed</h3>
                {/* <p>Trend of points redeemed over time</p> */}
              </div>
            </div>
            <div style={{ marginTop: "20px", overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr
                    style={{
                      backgroundColor: "#f8f9fa",
                      borderBottom: "1px solid #dee2e6",
                    }}
                  >
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontWeight: 600,
                      }}
                    >
                      Location
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "right",
                        fontWeight: 600,
                      }}
                    >
                      Points Redeemed
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {locationWisePointsSummary.map((item, index) => (
                    <tr
                      key={`expired-${index}`}
                      style={{
                        borderBottom: "1px solid #dee2e6",
                        backgroundColor: index % 2 === 0 ? "#fff" : "#f8f9fa",
                      }}
                    >
                      <td
                        style={{
                          padding: "12px",
                          borderRight: "1px solid #dee2e6",
                        }}
                      >
                        {item.location || "N/A"}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "right",
                          color: "#FF3B30",
                        }}
                      >
                        {item.pointsRedeemed?.toLocaleString() || "0"}
                      </td>
                    </tr>
                  ))}
                  {locationWisePointsSummary.length === 0 && (
                    <tr>
                      <td
                        colSpan={2}
                        style={{
                          padding: "20px",
                          textAlign: "center",
                          color: "#6c757d",
                        }}
                      >
                        No points redeemed data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Notifications Dashboard */}
      <section style={{ marginTop: "40px" }}>
        <h2 style={{ marginBottom: "20px", fontSize: "20px", fontWeight: 600 }}>
          Notifications Dashboard
        </h2>
        <div className="panel-grid four">
          {notificationStats.map((stat) => (
            <div
              className="cards stat_card"
              key={stat.id}
              onClick={() => {
                if (stat.isClickable && stat.id === "birthdays") {
                  handleBirthdayListModalOpen();
                }
                if (stat.isClickable && stat.id === "birthdayNotifications") {
                  // handleBirthdayNotificationsModalOpen();
                  navigate("/BirthdayNotifications", {
                    state: {
                      startDate: customDateRange.startDate,
                      endDate: customDateRange.endDate,
                    },
                  });
                }
              }}
              style={stat.isClickable ? { cursor: "pointer" } : {}}
            >
              <p className="label">{stat.label}</p>
              <h3>{stat.value}</h3>
              <span className="muted">{stat.helper}</span>
            </div>
          ))}
        </div>
        <div className="cards chart_card" style={{ marginTop: "20px" }}>
          <div className="section_heading">
            <div>
              <h3>Notifications Sent vs Opened</h3>
              <p>Daily comparison for the selected period</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={notificationsComparison} barGap={12}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="opened" radius={[8, 8, 0, 0]} fill="#34B53A" />
              <Bar dataKey="sent" radius={[8, 8, 0, 0]} fill="#2F66F6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Platform & Version Stats */}
      <section style={{ marginTop: "40px" }}>
        <h2 style={{ marginBottom: "20px", fontSize: "20px", fontWeight: 600 }}>
          Platform & Version Stats
        </h2>
        <div className="panel-grid three">
          {platformStats.map((stat) => (
            <div className="cards stat_card" key={stat.id}>
              <p className="label">{stat.label}</p>
              <h3>{stat.value}</h3>
              <span className="muted">{stat.helper}</span>
            </div>
          ))}
        </div>
        <div className="panel-grid two" style={{ marginTop: "20px" }}>
          <div className="cards chart_card">
            <div className="section_heading">
              <div>
                <h3>Platform Distribution</h3>
                <p>Android vs iOS</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={platformDistribution}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={70}
                  outerRadius={110}
                  paddingAngle={3}
                >
                  {platformDistribution.map((entry, index) => (
                    <Cell
                      key={entry.name}
                      fill={CHART_COLORS[index % CHART_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="cards chart_card">
            <div className="section_heading">
              <div>
                <h3>App Version Distribution</h3>
                <p>Version distribution across all users</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={versionUsage} barSize={30}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="version" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="users" radius={[10, 10, 0, 0]} fill="#2F66F6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Top 10 Active & Inactive Customers */}
      <section style={{ marginTop: "40px" }}>
        <h2 style={{ marginBottom: "20px", fontSize: "20px", fontWeight: 600 }}>
          Top 10 Active & Inactive Customers
        </h2>
        <div className="panel-grid two">
          <div className="cards">
            <div className="section_heading">
              <h3>Top 10 Active Customers</h3>
            </div>
            <div className="table_container">
              <table>
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Customer</th>
                    <th>Mobile</th>
                    <th>Logins</th>
                    {/* <th>Points</th> */}
                    <th>Last Login</th>
                  </tr>
                </thead>
                <tbody>
                  {activeCustomers.map((customer) => (
                    <tr key={customer.rank}>
                      <td>{customer.rank || "-"}</td>
                      <td>{customer.name || "N/A"}</td>
                      <td>{customer.mobile || "N/A"}</td>
                      <td>{customer.logins || 0}</td>
                      <td>{customer.lastLoginActivity || "Never logged in"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="cards">
            <div className="section_heading">
              <h3>Top 10 Inactive Customers</h3>
            </div>
            <div className="table_container">
              <table>
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Customer</th>
                    <th>Mobile</th>
                    <th>Logins</th>
                    {/* <th>Points</th> */}
                    <th>Last Login</th>
                  </tr>
                </thead>
                <tbody>
                  {inactiveCustomers.map((customer) => (
                    <tr key={customer?.rank}>
                      <td>{customer?.rank}</td>
                      <td>{customer?.name}</td>
                      <td>{customer?.mobile}</td>
                      <td>{customer?.logins}</td>
                      {/* <td>{customer.points}</td> */}
                      <td>
                        {customer?.lastLoginActivity || "Never logged in"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Daily App Usage */}
      <section style={{ marginTop: "40px" }}>
        <h2 style={{ marginBottom: "20px", fontSize: "20px", fontWeight: 600 }}>
          Daily App Usage
        </h2>
        <div className="panel-grid two">
          {dailyUsageStats.map((stat) => (
            <div className="cards stat_card" key={stat.id}>
              <p className="label">{stat.label}</p>
              <h3>{stat.value}</h3>
              <span className="muted">{stat.helper}</span>
            </div>
          ))}
        </div>
        <div className="cards chart_card" style={{ marginTop: "20px" }}>
          <div className="section_heading">
            <div>
              <h3>Daily Active Users Trend</h3>
              <p>Trend of daily active users over time</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart data={dailyActiveUsers}>
              <defs>
                <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2F66F6" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#2F66F6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="users"
                stroke="#2F66F6"
                fill="url(#colorUsers)"
                strokeWidth={3}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Referral Performance */}
      <section style={{ marginTop: "40px" }}>
        <h2 style={{ marginBottom: "20px", fontSize: "20px", fontWeight: 600 }}>
          Referral Performance
        </h2>
        <div className="panel-grid three">
          {referralSummary.map((stat) => (
            <div className="cards stat_card" key={stat.id}>
              <p className="label">{stat.label}</p>
              <h3>{stat.value}</h3>
              <span className="muted">{stat.helper}</span>
            </div>
          ))}
        </div>
        <div className="cards chart_card" style={{ marginTop: "20px" }}>
          <div className="section_heading">
            <div>
              <h3>Referral Activity</h3>
              <p>Converted vs points earned vs referrals sent</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={referralActivity}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="day" />
              <YAxis yAxisId="left" orientation="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Bar
                yAxisId="left"
                dataKey="converted"
                fill="#34B53A"
                radius={[6, 6, 0, 0]}
              />
              <Bar
                yAxisId="left"
                dataKey="sent"
                fill="#2F66F6"
                radius={[6, 6, 0, 0]}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="pointsEarned"
                stroke="#FF8A48"
                strokeWidth={3}
                dot={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="panel-grid three" style={{ marginTop: "20px" }}>
          {referralAverages.map((stat) => (
            <div className="cards stat_card" key={stat.id}>
              <p className="label">{stat.label}</p>
              <h3>{stat.value}</h3>
            </div>
          ))}
        </div>
      </section>

      <BirthdayListModal
        open={isBirthdayModalOpen}
        onClose={() => setIsBirthdayModalOpen(false)}
        birthdays={upcomingBirthdays}
      />
    </div>
  );
};
export default Dashboard;
